import type {OnCompileContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {Evaluator, Version} from "@cm/graph-v3"
import {versionChain} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {TemplateNode} from "#template/template-node/template-node"
import type {BooleanLike, NumberLike} from "#template/nodes/types"
import {booleanLike} from "#template/nodes/boolean/fundamental"
import {numberLike} from "#template/nodes/number/fundamental"
import type {SceneNodes} from "#template/interfaces/scene-object"
import {ThisStructID} from "#template/runtime-graph/types"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import {visitNone} from "#template/template-node/visit-helpers"

const TD = TypeDescriptors

export type RenderParameters = {
    percentage: NumberLike
    samples: NumberLike
    textureStreaming?: BooleanLike
    renderTiling: "off" | "on" | "auto"
    denoiseTiling: "off" | "on" | "auto"
}

export type RenderConstructorParameters = Omit<RenderParameters, "percentage" | "textureStreaming" | "renderTiling" | "denoiseTiling"> & {
    percentage?: NumberLike
    textureStreaming?: BooleanLike
    renderTiling?: "off" | "on" | "auto"
    denoiseTiling?: "off" | "on" | "auto"
}

type V0 = {
    width: NumberLike
    height: NumberLike
    samples: NumberLike
}

// gpu/cloud used to be per-template choices; all renders run on cloud GPU now.
type V1 = RenderParameters & {
    gpu: BooleanLike
    cloud: BooleanLike
}

type V2 = RenderParameters

const v0: Version<V0, V1> = {
    toNextVersion: (parameters) => {
        return {
            gpu: false,
            cloud: false,
            textureStreaming: false,
            renderTiling: "auto",
            denoiseTiling: "auto",
            percentage: 100,
            samples: parameters.samples,
        }
    },
}

const v1: Version<V1, V2> = {
    toNextVersion: ({gpu: _gpu, cloud: _cloud, ...parameters}) => parameters,
}

const renderDefinition: TemplateNodeDefinition<RenderParameters, RenderConstructorParameters> = {
    nodeClass: "Render",
    validation: {
        parameters: z.object({
            percentage: z._default(numberLike, 100),
            samples: numberLike,
            textureStreaming: z.optional(booleanLike),
            renderTiling: z._default(z.enum(["off", "on", "auto"]), "auto"),
            denoiseTiling: z._default(z.enum(["off", "on", "auto"]), "auto"),
        }),
        returns: getTemplateNodeReturns(),
    },
    versionChain: versionChain([v0, v1]),
}

@registerTemplateGraphNode
export class Render extends TemplateNode<RenderParameters, RenderConstructorParameters> {
    public override get definition() {
        return renderDefinition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<RenderParameters> {
        const {evaluator, currentTemplate, templateDepth, path} = context
        const {displayList} = currentTemplate
        const {templateScope} = evaluator
        const {percentage, samples, textureStreaming, renderTiling, denoiseTiling} = this.parameters

        const scope = evaluator.getScope(this)

        const nullToZero = (number: BuilderInlet<number | null>, uniqueId: string) => scope.pureLambda(number, (number) => number ?? 0, uniqueId)
        const nullToHundred = (number: BuilderInlet<number | null>, uniqueId: string) => scope.pureLambda(number, (number) => number ?? 100, uniqueId)
        const nullToTrue = (boolean: BuilderInlet<boolean | null>, uniqueId: string) => scope.pureLambda(boolean, (boolean) => boolean ?? true, uniqueId)

        const renderSettings = templateScope.struct<SceneNodes.RenderSettings>("RenderSettings", {
            type: "RenderSettings",
            templateDepth,
            id: ThisStructID,
            path: scope.pureLambda(scope.tuple(path, this), ([path, instance]) => [...path, instance], "newPath", {
                output: TD.ObjectPath,
            }),
            percentage: nullToHundred(evaluator.evaluate(percentage), "percentage"),
            samples: nullToZero(evaluator.evaluate(samples), "samples"),
            // All renders run on cloud GPU; the local render farm and CPU rendering are retired.
            gpu: true,
            cloud: true,
            textureStreaming: nullToTrue(evaluator.evaluate(textureStreaming ?? null), "textureStreaming"),
            renderTiling,
            denoiseTiling,
        })

        displayList.push(renderSettings)

        return visitNone(this.parameters)
    }
}
